
import { Users, Map, Calendar, Share2 } from 'lucide-react';
import HeroSection from '@/components/ui-custom/HeroSection';

const About = () => {
  const renderRiderStories = () => {
    return;
    return (
        <section className="section-padding bg-adv-charcoal border-y border-adv-darkGray">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-lg text-white mb-4">Rider Stories</h2>
              <p className="text-adv-lightGray max-w-2xl mx-auto">
                Hear what our community members have to say about their Exodus ADV experiences.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="glass-card rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
                    <img 
                      src="/images/testimonial-1.jpg" 
                      alt="Sarah T." 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Sarah T.</h4>
                    <p className="text-adv-lightGray text-sm">Portland, OR</p>
                  </div>
                </div>
                <p className="text-adv-lightGray italic">
                  "The Utah Rally was my first real ADV experience, and I was nervous about keeping up. The Exodus team made sure everyone felt welcome regardless of skill level. I left with new skills and friendships that will last a lifetime."
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
                    <img 
                      src="/images/testimonial-2.jpg" 
                      alt="Mike R." 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Mike R.</h4>
                    <p className="text-adv-lightGray text-sm">Denver, CO</p>
                  </div>
                </div>
                <p className="text-adv-lightGray italic">
                  "I've been to many motorcycle events over the years, but Exodus rallies stand apart. The combination of great routes, like-minded people, and the overall organization makes these events the highlight of my riding season."
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
                    <img 
                      src="/images/testimonial-3.jpg" 
                      alt="Carlos J." 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Carlos J.</h4>
                    <p className="text-adv-lightGray text-sm">Austin, TX</p>
                  </div>
                </div>
                <p className="text-adv-lightGray italic">
                  "The Ride Hub has transformed how I find riding buddies. Posted a local ride last month and had 12 people show up! Now we ride together regularly. Exodus ADV has created something special for the community."
                </p>
              </div>
            </div>
          </div>
        </section>
    )
  }

  return (
    <div className="bg-adv-dark min-h-screen">
      <HeroSection
        title="About Exodus ADV"
        subtitle="Our mission is to make adventure riding accessible, community-driven, and unforgettable."
        backgroundImage="/images/about-hero.jpg"
      />
      
      <main id="content">
        {/* Mission & Story */}
        <section className="section-padding relative">
          {/* Background Image with Overlay */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url(/static/core/our-story-banner.png)'
            }}
          />
          <div className="absolute inset-0 bg-adv-dark/80"></div>
          
          <div className="container-custom relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="heading-lg text-white mb-6">Our Story</h2>
                <div className="prose prose-invert max-w-none">
                  <p className="text-adv-lightGray mb-4">
                    ExodusADV started the way most great rides do: with a few friends, a rough idea, and a deep need to get out and explore. From the beginning, I found myself doing the same thing over and over: figuring out the best roads, rallying people to show up, and trying to create something that actually felt like an adventure instead of just another ride.
                  </p>
                  <p className="text-adv-lightGray mb-4">
                    The more I rode, the more it became clear… the ADV world is full of incredible people, but it's scattered. Finding a good group is hard. Finding rides that are actually well-organized is harder. And almost nobody wants to take the lead. I've always been the one to step up and say, "let's make this happen." That hasn't changed.
                  </p>
                  <p className="text-adv-lightGray mb-4">
                    ExodusADV is about turning that frustration into a solution. It's a home for riders who want the real thing: legit adventure, real connections, and rides that don't suck. Our official rallies are run with purpose but keep it raw with campfires, dirty bikes, and new friendships formed over shared experience.
                  </p>
                  <p className="text-adv-lightGray">
                    More than anything, ExodusADV exists to close the gap between "I want to ride" and actually doing it with the people you connect with. Whether you're just getting started or you've been riding for decades, we're building a place where you can find your crew, your next ride, and your next challenge.
                  </p>
                </div>
              </div>
              
              <div className="relative"></div>
            </div>
          </div>
        </section>
        
        {/* Values */}
        <section className="section-padding bg-adv-charcoal border-y border-adv-darkGray">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-lg text-white mb-4">Our Values</h2>
              <p className="text-adv-lightGray max-w-2xl mx-auto">
                The principles that guide everything we do at Exodus ADV.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="glass-card rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-adv-orange/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-adv-orange" size={24} />
                </div>
                <h3 className="font-bold text-lg mb-2">Community</h3>
                <p className="text-adv-lightGray">
                  We believe the best adventures are shared. We create spaces for riders to connect, learn from each other, and build lasting friendships.
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-adv-orange/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Map className="text-adv-orange" size={24} />
                </div>
                <h3 className="font-bold text-lg mb-2">Exploration</h3>
                <p className="text-adv-lightGray">
                  We're dedicated to discovering new trails, routes, and destinations that challenge riders and showcase the beauty of our natural world.
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-adv-orange/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="text-adv-orange" size={24} />
                </div>
                <h3 className="font-bold text-lg mb-2">Accessibility</h3>
                <p className="text-adv-lightGray">
                  Adventure riding should be for everyone. We create events and content that welcomes riders of all skill levels and backgrounds.
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-adv-orange/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Share2 className="text-adv-orange" size={24} />
                </div>
                <h3 className="font-bold text-lg mb-2">Knowledge Sharing</h3>
                <p className="text-adv-lightGray">
                  We're committed to creating and sharing resources that help riders grow their skills and enjoy the adventure motorcycle lifestyle to its fullest.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Timeline */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-lg text-white mb-4">Our Journey</h2>
              <p className="text-adv-lightGray max-w-2xl mx-auto">
                The milestones that have shaped Exodus ADV.
              </p>
            </div>
            
            <div className="max-w-3xl mx-auto">
              {/* Timeline items */}
              <div className="relative">
                {/* Line */}
                <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-0.5 bg-adv-darkGray transform md:translate-x-[-0.5px]"></div>
                
                {/* Pre-2021 */}
                <div className="relative flex flex-col md:flex-row items-start mb-12">
                  <div className="flex flex-col items-center md:w-1/2 md:pr-8">
                    <div className="w-8 h-8 rounded-full bg-adv-orange flex items-center justify-center z-10 order-1 md:order-none">
                      <span className="text-white text-sm font-bold">1</span>
                    </div>
                    <div className="ml-12 md:ml-0 glass-card rounded-lg p-5 w-full order-2 md:order-none md:mt-0 mt-4">
                      <h3 className="font-bold text-lg mb-1">Pre-2021: Just Riding</h3>
                      <p className="text-adv-lightGray">
                        Buying a motorcycle out of college, riding, and enjoying the road.
                      </p>
                    </div>
                  </div>
                  <div className="md:w-1/2"></div>
                </div>
                
                {/* 2021 */}
                <div className="relative flex flex-col md:flex-row items-start mb-12">
                  <div className="md:w-1/2"></div>
                  <div className="flex flex-col items-center md:w-1/2 md:pl-8">
                    <div className="w-8 h-8 rounded-full bg-adv-orange flex items-center justify-center z-10 order-1 md:order-none">
                      <span className="text-white text-sm font-bold">2</span>
                    </div>
                    <div className="ml-12 md:ml-0 glass-card rounded-lg p-5 w-full order-2 md:order-none md:mt-0 mt-4">
                      <h3 className="font-bold text-lg mb-1">2021: The Beginning</h3>
                      <p className="text-adv-lightGray">
                        Organizing and leading rides with friends turned from day rides to small rallies with partners to multi-day BDR trips.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* 2022-2024 */}
                <div className="relative flex flex-col md:flex-row items-start mb-12">
                  <div className="flex flex-col items-center md:w-1/2 md:pr-8">
                    <div className="w-8 h-8 rounded-full bg-adv-orange flex items-center justify-center z-10 order-1 md:order-none">
                      <span className="text-white text-sm font-bold">3</span>
                    </div>
                    <div className="ml-12 md:ml-0 glass-card rounded-lg p-5 w-full order-2 md:order-none md:mt-0 mt-4">
                      <h3 className="font-bold text-lg mb-1">2022-2024: Networking</h3>
                      <p className="text-adv-lightGray">
                        Learning how the online communities translate to real rides and understanding how ride leads turn into organizers and influencers over time.
                      </p>
                    </div>
                  </div>
                  <div className="md:w-1/2"></div>
                </div>
                
                {/* 2025 */}
                <div className="relative flex flex-col md:flex-row items-start">
                  <div className="md:w-1/2"></div>
                  <div className="flex flex-col items-center md:w-1/2 md:pl-8">
                    <div className="w-8 h-8 rounded-full bg-adv-orange flex items-center justify-center z-10 order-1 md:order-none">
                      <span className="text-white text-sm font-bold">4</span>
                    </div>
                    <div className="ml-12 md:ml-0 glass-card rounded-lg p-5 w-full order-2 md:order-none md:mt-0 mt-4">
                      <h3 className="font-bold text-lg mb-1">2025: Put it to Practice</h3>
                      <p className="text-adv-lightGray">
                        Work on ExodusADV begins with the goal of getting people connected, whether that's through day rides or longer rallies.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Team */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-lg text-white mb-4">Meet the Team</h2>
              <p className="text-adv-lightGray max-w-2xl mx-auto">
                The passionate riders behind Exodus ADV.
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-6">
              <div className="glass-card rounded-lg p-5 text-center">
                <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4">
                  <img 
                    src="/static/core/ward-exodus-profile.png" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-bold mb-1">Ward</h3>
                <p className="text-adv-orange text-sm mb-3">Founder</p>
                <p className="text-adv-lightGray text-sm">
                  An obsessive adventure rider who is always seeking to connect people, build community, and experience adventures.
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-5 text-center">
                <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-4">
                  <img 
                    src="/static/core/ryan-exodus-profile.png" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-bold mb-1">Ryan</h3>
                <p className="text-adv-orange text-sm mb-3">Chief Technologist</p>
                <p className="text-adv-lightGray text-sm">
                  An experienced software professional who has a passion for building for adventure.
                </p>
              </div>
            </div>
          </div>
        </section>

        {renderRiderStories()}
        
        {/* CTA */}
        <section className="section-padding bg-adv-charcoal border-y border-adv-darkGray">
          <div className="container-custom">
            <div className="glass rounded-xl p-8 md:p-12 text-center">
              <h2 className="heading-lg mb-4">Join the Exodus ADV Community</h2>
              <p className="text-adv-lightGray max-w-2xl mx-auto mb-8">
                Whether you're looking for your next adventure or wanting to share your own, there's a place for you in our community.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a href="/ride-hub" className="btn-primary">
                  Find a Ride
                </a>
                <a href="/rallies" className="btn-secondary">
                  Join a Rally
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default About;
